<template>
  <!-- Desktop user menu -->
  <div
    v-if="layout === 'horizontal'"
    ref="profileMenuRef"
    class="relative ml-3"
  >
    <div>
      <button
        id="user-menu-button"
        type="button"
        class="relative flex max-w-xs items-center rounded-full bg-gray-200 dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900"
        aria-expanded="false"
        aria-haspopup="true"
        @click="toggleProfileMenu"
      >
        <span class="sr-only">{{ t('app.actions.open_user_menu') }}</span>
        <div class="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-900 dark:text-white">
          {{ userInitials }}
        </div>
      </button>
    </div>

    <!-- Dropdown menu -->
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isProfileMenuOpen"
        class="absolute right-0 z-10 mt-2 w-52 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none"
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="user-menu-button"
        tabindex="-1"
      >
        <UserMenuItems
          @close="isProfileMenuOpen = false"
          @logout="handleLogout"
        />
      </div>
    </transition>
  </div>

  <!-- Vertical sidebar user section -->
  <div
    v-else-if="layout === 'vertical'"
    class="border-t border-gray-200 dark:border-gray-700 p-4 mt-auto"
  >
    <div class="flex items-center mb-3">
      <div class="flex-shrink-0">
        <div class="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-900 dark:text-white">
          {{ userInitials }}
        </div>
      </div>
    </div>
    
    <!-- Alerts Badge - Only show in restaurant subdomains and Cloud (not Electron) -->
    <div
      v-if="showAlerts"
      class="mb-3"
    >
      <button
        class="relative w-full flex items-center justify-center rounded-md bg-gray-100 dark:bg-gray-800 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 touch-manipulation"
        @click="handleNotificationClick"
      >
        <BellAlertIcon class="h-5 w-5" />
        <span class="ml-2 text-sm">Alertas</span>
        <span
          v-if="unreadAlertCount > 0"
          class="absolute top-1 right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[18px]"
        >
          {{ unreadAlertCount > 9 ? '9+' : unreadAlertCount }}
        </span>
      </button>
    </div>

    <UserMenuItems @logout="handleLogout" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { BellAlertIcon } from '@heroicons/vue/24/outline';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { hasRestaurantContext } from '@/utils/subdomain';
import { platformFeatures } from '@/utils/platform';
import UserMenuItems from './UserMenuItems.vue';

interface Props {
  layout?: 'horizontal' | 'vertical';
  unreadAlertCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
  layout: 'horizontal',
  unreadAlertCount: 0
});

const emit = defineEmits<{
  'notification-click': [];
  'logout': [];
}>();

const authStore = useAuthStore();
const { t } = useI18n();
const { canViewSubscription } = usePermissions();

const isProfileMenuOpen = ref(false);
const profileMenuRef = ref(null);

const userInitials = computed(() => {
  if (!authStore.user) return 'U';
  const name = authStore.user.name || authStore.user.email;
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
});


const showAlerts = computed(() => {
  return platformFeatures.cloudOnly.subscription && 
         canViewSubscription.value && 
         hasRestaurantContext();
});

function toggleProfileMenu() {
  isProfileMenuOpen.value = !isProfileMenuOpen.value;
}

function handleNotificationClick() {
  emit('notification-click');
}

function handleLogout() {
  emit('logout');
  isProfileMenuOpen.value = false;
}

</script>
